import type { SharedData } from "@src/context/ApplicationContext";
import type { SalesforceAppointmentDetailsPayload } from "@src/services/canvas/outbound/types";
import type {
  SalesforceCanvasInsurancePayload,
  SalesforceCanvasPatientPayload,
  SalesforceCanvasScreenerPayload,
} from "@src/services/canvas/prefill/types";
import { normalizeString } from "@src/utils/string-utils";
import type { OutboundPayload } from "./types";

// ── Reverse-mapping tables (app-internal → Salesforce) ──────────────

const GENDER_TO_SF: Record<string, "M" | "F"> = { m: "M", f: "F" };

const RELATIONSHIP_TO_SF: Record<string, "Parent" | "Spouse" | "Other"> = {
  child: "Parent",
  spouse: "Spouse",
  other: "Other",
};

/** App date `MM/DD/YYYY` → Salesforce ISO `YYYY-MM-DD`. */
const APP_DATE_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/**
 * Converts app date strings to Salesforce format, returning undefined for invalid
 */
function toSalesforceDate(value?: string | null): string | undefined {
  if (!value) return undefined;
  const m = APP_DATE_PATTERN.exec(value);
  return m ? `${m[3]}-${m[1]}-${m[2]}` : undefined;
}

/** Strip non-digits; return raw digit string for 10-digit NANP numbers. */
function toSalesforcePhone(value?: string): string | undefined {
  if (!value) return undefined;
  const digits = value.replaceAll(/\D/g, "");
  return digits.length === 10 ? digits : undefined;
}

function toScalarId(value: unknown): string | number | undefined {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }
  return normalizeString(value);
}

/** Returns a new object with all null/undefined values removed. */
function compact<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, v]) => {
      return v != null;
    }),
  ) as Partial<T>;
}

/**
 * Builds the outbound Canvas sync payload from the current ApplicationContext
 * SharedData snapshot.  This is a pure function with no side effects.
 *
 * The patient/insurance field names intentionally mirror the inbound prefill
 * schema (SalesforceCanvasPatientPayload / SalesforceCanvasInsurancePayload)
 * so Salesforce handles one schema in both directions.
 */
export function buildOutboundPayload(
  data: SharedData,
  route: string,
  isScheduled: boolean = false,
): OutboundPayload {
  const patient = buildPatientBlock(data);
  const insurance = buildInsuranceBlock(data);
  const screener = buildScreenerOutboundBlock(data);
  const appointmentDetails = buildAppointmentDetailsBlock(data);

  return compact({
    route,
    patient,
    insurance,
    screener,
    ...(isScheduled ? appointmentDetails : {}),
  }) as OutboundPayload;
}

// ── Block builders ──────────────────────────────────────────────────

function formatPolicyHolderFlag(value?: boolean): "Yes" | "No" | undefined {
  if (value === undefined) return undefined;
  return value ? "Yes" : "No";
}

function buildPatientBlock(data: SharedData): SalesforceCanvasPatientPayload | undefined {
  const m = data.memberDetails;

  const block = compact({
    zip: data.zip,
    dateOfBirth: toSalesforceDate(data.dob),
    genderAtBirth: data.gender ? GENDER_TO_SF[data.gender] : undefined,
    firstName: m?.firstName,
    lastName: m?.lastName,
    phone: toSalesforcePhone(m?.phone),
    email: m?.email,
    language: m?.language,
    streetAddress1: m?.streetAddress1,
    streetAddress2: m?.streetAddress2,
    city: m?.city,
    state_code: m?.state_code,
  });

  return Object.keys(block).length > 0 ? (block as SalesforceCanvasPatientPayload) : undefined;
}

function buildInsuranceBlock(data: SharedData): SalesforceCanvasInsurancePayload | undefined {
  if (data.paymentMethod === undefined) return undefined;

  const isSelfPay = data.paymentMethod === "self_pay";

  if (isSelfPay) {
    return { selfPay: true };
  }

  const insurancePlanName =
    data.insurancePolicy && "name" in data.insurancePolicy && data.insurancePolicy.name
      ? data.insurancePolicy.name
      : undefined;

  const payerId =
    data.insurancePolicy && "payer_id" in data.insurancePolicy
      ? toScalarId(data.insurancePolicy.payer_id)
      : undefined;

  return compact({
    selfPay: false,
    packageId: data.selectedPolicy,
    payerId: payerId,
    insurancePlanType: insurancePlanName,
    memberId: data.memberId,
    isPrimaryPolicyHolder: formatPolicyHolderFlag(data.isPrimaryPolicyHolder),
    policyholderFirstName: data.policyholderFirstName,
    policyholderLastName: data.policyholderLastName,
    policyholderDOB: toSalesforceDate(data.policyholderDOB),
    policyholderGenderAtBirth: data.policyholderGender
      ? GENDER_TO_SF[data.policyholderGender]
      : undefined,
    relationshipToPolicyholder: data.relationshipToPolicyholder
      ? RELATIONSHIP_TO_SF[data.relationshipToPolicyholder]
      : undefined,
  }) as SalesforceCanvasInsurancePayload;
}

function boolToYesNo(value: boolean | undefined): "Yes" | "No" | null {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return null;
}

function buildScreenerOutboundBlock(data: SharedData): SalesforceCanvasScreenerPayload | undefined {
  const a = data.patientScreenerAnswers;
  if (!a) return undefined;
  return {
    hospitalizedPast30Days: boolToYesNo(a.hospitalized),
    activeEatingDisorder: boolToYesNo(a.eatingDisorder),
    seriousMentalIllness: boolToYesNo(a.seriousMentalIllness),
    longActingInjectables: boolToYesNo(a.longActingInjectable),
    opioidTreatmentMedications: boolToYesNo(a.opioidTreatment),
    controlledSubstances: boolToYesNo(a.controlledSubstances),
  };
}

function buildAppointmentDetailsBlock(
  data: SharedData,
): SalesforceAppointmentDetailsPayload | undefined {
  const appointmentDetails = data.appointmentDetails;
  return appointmentDetails?.providerId ? { providerId: appointmentDetails.providerId } : undefined;
}
